'use client';
import { Separator } from '@ahafood/ui/components/separator';
import Link from 'next/link';
import { useMemo } from 'react';
import BannerSkeleton from './banner-skeleton';
import { useGetHomeBanner } from '@/app/[locale]/(main)/store/components/items/cms/home-banner/useGetHomeBanner';
import FreeModeSwiper from '@/app/[locale]/(main)/store/components/items/free-mode-swiper';
import { trackEvent } from '@/lib/event-tracking';
import { useIsMobile } from '@/lib/is-mobile';
import { useAuthStore } from '@/store';
import { useAiFoodOrderStore } from '@/store/use-ai-store';

export default function NewStoreBanner() {
  const isMobile = useIsMobile();
  const { user } = useAuthStore();
  const cityId = useAiFoodOrderStore.use.city_id();

  // Fetch banner data using the hook
  const { data: bannerData, isLoading } = useGetHomeBanner();

  const localizedType = (cityId: string): string => {
    switch (cityId) {
      case 'SGN':
        return 'aifood-sgn';
      case 'DAD':
        return 'aifood-dad';
      case 'HAN':
        return 'aifood-han';
      default:
        return 'aifood';
    }
  };

  // Đảm bảo luôn lấy ra mảng banner
  const homeBannerRes = useMemo(() => {
    if (!bannerData) return [];
    if (Array.isArray(bannerData)) return bannerData;
    // Nếu data là object kiểu { data: { attributes: { items: [...] } } }
    if (
      bannerData &&
      typeof bannerData === 'object' &&
      'data' in bannerData &&
      (bannerData as any).data?.attributes?.items
    ) {
      return (bannerData as any).data.attributes.items;
    }
    return [];
  }, [bannerData]);

  // Filter banners by type
  const homeBannerData = useMemo(
    () => homeBannerRes.filter((e) => e.type === 'aifood'),
    [homeBannerRes]
  );
  const localizedBannerData = useMemo(
    () => homeBannerRes.filter((e) => e.type === localizedType(cityId ?? '')),
    [cityId, homeBannerRes]
  );
  const combinedBannerData = useMemo(() => {
    return [...homeBannerData, ...localizedBannerData];
  }, [homeBannerData, localizedBannerData]);

  // const hasActiveFilters = !!(deal ?? query ?? category ?? isSearchByCategory);

  // Show skeleton while loading
  if (isLoading) {
    return <BannerSkeleton />;
  }

  // // Hide on error or if filters are active or no banners
  // if (error ?? hasActiveFilters ?? combinedBannerData.length === 0) {
  //   return null;
  // }

  const handleBannerClick = (banner: any) => {
    trackEvent('click_button', {
      action_time: Math.floor(Date.now() / 1000),
      button_name: 'click_banner',
      banner_id: banner?.desktop_image?.data?.id ?? '',
      user_id: user?.cunstomerId,
    });
  };

  // Render banners
  return (
    <section className="bg-transparent pb-4 md:py-8 [&+section.ahf-deal]:-mt-10 md:[&+section.ahf-deal]:mt-0 overflow-hidden">
      <div className="w-full flex items-center justify-center p-4 pt-2 text-neutral-50 gap-10 bg-transparent mb-4">
        <Separator className="h-px w-full flex-1" />
        <span className="font-semibold"> Nội dung nổi bật</span>
        <Separator className="h-px w-full flex-1" />
      </div>
      <div className="mx-auto w-full max-w-screen-xl px-4 md:px-6">
        <FreeModeSwiper
          enableSingMode={true}
          navigation={false}
          slidesView={2}
          className="w-full !overflow-visible"
          spaceBetween={16}
        >
          {combinedBannerData.length === 0 ? (
            <FreeModeSwiper.Slide
              style={{
                width: isMobile ? '100%' : '49%',
                aspectRatio: '1000/443',
              }}
            ></FreeModeSwiper.Slide>
          ) : (
            combinedBannerData.map((banner, index) => (
              <FreeModeSwiper.Slide
                key={index}
                style={{
                  width: isMobile ? '100%' : '49%',
                  aspectRatio: '1000/443',
                }}
              >
                <Link
                  href={banner?.link ?? ''}
                  onClick={() => handleBannerClick(banner)}
                >
                  <div className="relative size-full overflow-hidden rounded-xl">
                    <div className="p-0">
                      <img
                        src={banner.desktop_image.data.attributes.url}
                        alt={
                          banner.desktop_image.data.id?.toString() || 'Banner'
                        }
                        className="w-full h-auto object-contain rounded-xl"
                        style={{
                          maxHeight: isMobile ? '180px' : '443px',
                          background: '#fff',
                        }}
                      />
                    </div>
                  </div>
                </Link>
              </FreeModeSwiper.Slide>
            ))
          )}
        </FreeModeSwiper>
      </div>
    </section>
  );
}
